import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Controller, useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
} from '@brand/components';
import {
  IFieldProps,
  IFieldStyles,
  ITextFieldSchema,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

const TextField = ({
  field,
  id,
  name,
}: IFieldProps<ITextFieldSchema>): JSX.Element => {
  const {
    label,
    placeholder,
    htmlInputType,
    isRequired,
    isDisabled,
    value,
    shouldDisplay,
    styles = {},
  } = field;

  const { t } = useTranslation('form');

  const fieldStyles = useStyles<IFieldStyles>('textField', styles);

  const { watch } = useFormContext();

  const errorMessage = useErrorMessage(name, label);
  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        {...fieldStyles.control}
      >
        <ComponentIsVisible when={!!label}>
          <FormLabel
            htmlFor={name}
            marginBottom="4px"
            variant="proRikApp"
            {...fieldStyles.label}
          >
            {label}
          </FormLabel>
        </ComponentIsVisible>
        <Controller
          defaultValue={value || ''}
          name={name}
          render={({ field }) => (
            <Input
              aria-label={field.name}
              autoComplete={
                htmlInputType === 'password' ? 'current-password' : 'off'
              }
              data-testid={id}
              isDisabled={isDisabled}
              name={field.name}
              onChange={field.onChange}
              placeholder={placeholder}
              sx={{
                color: 'heading',
                fontWeight: 'medium',
                fontSize: '1rem',
              }}
              type={htmlInputType || 'text'}
              value={field.value}
              {...fieldStyles.input}
            />
          )}
        />

        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage
            justifyContent="flex-end"
            textAlign="right"
            {...fieldStyles.errorMessage}
          >
            {t(`validations.${errorMessage}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};

export default TextField;
